import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const saveAppointments = (appointments) => {
  localStorage.setItem("appointments", JSON.stringify(appointments));
};

// ============================================
// MAIN COMPONENT
// ============================================

const SettleDuePayment = ({ patientDetails, reset }) => {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Load payment data from the appointment
  useEffect(() => {
    if (patientDetails && patientDetails.id) {
      const appointments = getAppointments();
      const appointment = appointments.find((apt) => apt.id === patientDetails.id);
      if (appointment) {
        setPaymentData({
          amount: appointment.paymentAmount || "",
          modeOfPayment: appointment.paymentMode || "",
        });
      }
    }
  }, [patientDetails]);

  const formik = useFormik({
    initialValues: {
      fees: paymentData?.amount || "",
      modePayment: paymentData?.modeOfPayment || "",
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      fees: Yup.string().required("Fees is required"),
      modePayment: Yup.string().required("Mode of Payment is required"),
    }),
    onSubmit: (values) => {
      handleEditPayment(values);
    },
  });

  const handleEditPayment = (values) => {
    if (!patientDetails || !patientDetails.id) {
      toast.error("Invalid appointment data.");
      return;
    }

    setLoading(true);

    // Get all appointments
    const appointments = getAppointments();
    const index = appointments.findIndex((apt) => apt.id === patientDetails.id);

    if (index === -1) {
      toast.error("Appointment not found.");
      setLoading(false);
      return;
    }

    // Update the appointment
    const appointment = appointments[index];
    const updatedAppointment = {
      ...appointment,
      paymentStatus: "PAID",
      paymentMode: values.modePayment,
      paymentAmount: parseFloat(values.fees),
      paymentDate: new Date().toISOString(),
      appointmentvisitStatus: "VISITED",
      updatedAt: new Date().toISOString(),
    };

    appointments[index] = updatedAppointment;
    saveAppointments(appointments);

    toast.success("Payment updated successfully!");
    setLoading(false);
    reset();
  };

  const handleUnpaidPayment = () => {
    if (!patientDetails || !patientDetails.id) {
      toast.error("Invalid appointment data.");
      return;
    }

    setLoading(true);

    const appointments = getAppointments();
    const index = appointments.findIndex((apt) => apt.id === patientDetails.id);

    if (index === -1) {
      toast.error("Appointment not found.");
      setLoading(false);
      return;
    }

    // Set back to UNPAID and PENDING
    const appointment = appointments[index];
    const updatedAppointment = {
      ...appointment,
      paymentStatus: "UNPAID",
      appointmentvisitStatus: "PENDING",
      paymentMode: null,
      paymentAmount: null,
      paymentDate: null,
      updatedAt: new Date().toISOString(),
    };

    appointments[index] = updatedAppointment;
    saveAppointments(appointments);

    toast.info("Payment marked as UNPAID.");
    setLoading(false);
    reset();
  };

  return (
    <>
      <div
        id="login-popup"
        tabIndex="-1"
        className="bg-black/50 overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 h-full items-center justify-center flex"
      >
        <div className="relative p-4 w-full max-w-xl h-full md:h-auto">
          <div className="relative bg-white rounded-lg shadow">
            <button
              type="button"
              className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
              onClick={reset}
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="#c6c7c7"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close popup</span>
            </button>
            <div className="p-5">
              <div className="text-center">
                <p className="mb-3 text-2xl font-semibold leading-5 text-slate-900">
                  Settle Due Payment
                </p>
              </div>
              {/* Form */}
              <form onSubmit={formik.handleSubmit}>
                <div className="overflow-x-auto">
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="fees"
                      className="block text-sm font-medium text-gray-700 m-2"
                    >
                      Fees
                    </label>
                    <input
                      type="number"
                      name="fees"
                      value={formik.values.fees}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="fees"
                      placeholder="Fees"
                      className="w-full py-3 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.fees && formik.errors.fees && (
                      <p className="text-red-500 text-sm">{formik.errors.fees}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-6 mt-4">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="modePayment"
                          value="upi"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.modePayment === "upi"}
                          className="form-radio h-5 w-5 text-blue-600"
                        />
                        <span className="text-gray-700">UPI</span>
                      </label>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="modePayment"
                          value="cash"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.modePayment === "cash"}
                          className="form-radio h-5 w-5 text-blue-600"
                        />
                        <span className="text-gray-700">Cash</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mt-8 mx-auto">
                  <div className="flex flex-wrap -m-2">
                    <div className="p-2 w-1/2">
                      <button
                        type="button"
                        onClick={handleUnpaidPayment}
                        disabled={loading}
                        className="w-full bg-red-400 rounded border border-gray-300 focus:border-indigo-500 focus:ring-2 text-base outline-none text-white py-1 px-16 leading-8 transition-colors duration-200 ease-in-out disabled:opacity-50"
                      >
                        Unpaid
                      </button>
                    </div>
                    <div className="p-2 w-1/2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-green-500 rounded border border-gray-300 focus:border-indigo-500 focus:ring-2 text-base outline-none text-white py-1 px-16 leading-8 transition-colors duration-200 ease-in-out disabled:opacity-50"
                      >
                        {loading ? "Updating..." : "Edit Payment"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {loading && <Spinner />}
      <ToastContainer />
    </>
  );
};

export default SettleDuePayment;

